import ProductSelector from "./product-selector";

declare global {
  interface Window {
    nhtTireSelectorData: {
      [key: string]: TireData;
    };
  }
}

export type TireData = {
  translations: {
    [key: string]: string;
  };
  products: ProductData[];
  equipmentEventNames: {
    [key: string]: string;
  };
};

export type ProductData = {
  usageGroup: string[];
  equipment: string[];
  season: string;
  position: string;
  sizes: SizeData[];
};

export type SizeData = {
  width: number;
  rim: number;
  position: string;
  size: string;
};

export type TireState = {
  equipment: string[];
  season: string;
  position: string;
  width: string;
  rim: string;
  size: string;
}


export default class TireSelector extends ProductSelector {

  tireState: TireState = {
    equipment: [],
    season: '',
    position: '',
    width: '',
    rim: '',
    size: '',
  };

  tireDom: {
    equipment: HTMLInputElement[];
    mobileEquipment: HTMLSelectElement;
    season: HTMLSelectElement;
    position: HTMLSelectElement;
    width: HTMLSelectElement;
    rim: HTMLSelectElement;
    size: HTMLSelectElement;
  };

  data: ProductData[];

  #equipmentEventNames = {};

  constructor(container: HTMLElement) {
    const elementId = container.id;
    const templateElement = document.querySelector('[data-template-nht-tire-product-card]') as HTMLScriptElement;
    const template = templateElement?.innerHTML || '';

    const elementData = window?.nhtTireSelectorData?.[elementId];

    const settings = {
      resultsTitleSingular: elementData?.translations?.resultsTitleSingular,
      resultsTitlePlural: elementData?.translations?.resultsTitlePlural,
      sliderPrevButtonText: elementData?.translations?.sliderPrevButtonText,
      sliderNextButtonText: elementData?.translations?.sliderNextButtonText,
      formLoadingClass: 'loading',
      productCardTemplate: template,
      elements: {
        form: container.querySelector('form') as HTMLFormElement,
        resultsContainer: container.querySelector('[data-nht-ps-results]') as HTMLElement,
        resultsTitle: container.querySelector('[data-nht-ps-results-title]') as HTMLElement,
        resultsNavContainer: container.querySelector('[data-nht-ps-slider-nav]') as HTMLElement,
        productsContainer: container.querySelector('[data-nht-ps-results-products]') as HTMLElement,
      }
    };

    // Parent handles everything after form submit
    super(container, settings);

    console.log('nht-tire-selector.init()');

    // This class handles the cascading inputs (aka one selection changes the next)
    // Note that different usage groups can have different inputs (trucks vs rest)
    this.tireDom = {
      equipment: Array.from(container.querySelectorAll('input[name="equipment[]"]')) as HTMLInputElement[],
      mobileEquipment: container.querySelector('select[name="mobileEquipment"]') as HTMLSelectElement,
      season: container.querySelector('select[name="season"]') as HTMLSelectElement,
      position: container.querySelector('select[name="position"]') as HTMLSelectElement,
      width: container.querySelector('select[name="width"]') as HTMLSelectElement,
      rim: container.querySelector('select[name="rim"]') as HTMLSelectElement,
      size: container.querySelector('select[name="size"]') as HTMLSelectElement,
    };

    this.data = elementData?.products || [];

    this.#equipmentEventNames = elementData?.equipmentEventNames || {};

    // Init if all some elements and data found (some fields are usage-group specific)
    if(this.tireDom.equipment && this.data.length) {
      this.init();
    }
  }

  init() {
    // Get current state from form elements
    this.tireState = this.getTireStateFromDom();
    this.updateUI();

    // Add event listeners
    this.tireDom.equipment.forEach((input) => {
      input.addEventListener('change', this.onEquipmentChange.bind(this));
    });
    this.tireDom?.mobileEquipment?.addEventListener('change', this.onMobileEquipmentChange.bind(this));
    this.tireDom?.season?.addEventListener('change', this.onSeasonChange.bind(this));
    this.tireDom?.position?.addEventListener('change', this.onPositionChange.bind(this));
    this.tireDom?.width?.addEventListener('change', this.onWidthChange.bind(this));
    this.tireDom?.rim?.addEventListener('change', this.onRimChange.bind(this));
    this.tireDom?.size?.addEventListener('change', this.onSizeChange.bind(this));
  }

  getTireStateFromDom() {
    return {
      equipment: this.tireDom.equipment.filter((input) => input.checked).map((input) => input.value),
      season: this.tireDom?.season?.value || '',
      position: this.tireDom?.position?.value || '',
      width: this.tireDom?.width?.value || '',
      rim: this.tireDom?.rim?.value || '',
      size: this.tireDom?.size?.value || '',
    };
  }

  onEquipmentChange(event: Event) {
    const target = event.target as HTMLInputElement;
    const value = target.value;

    // Update state
    if (target.checked) {
      this.tireState.equipment.push(value);
    } else {
      this.tireState.equipment = this.tireState.equipment.filter((item) => item !== value);
    }
    this.tireState.season = '';
    this.tireState.position = '';
    this.tireState.width = '';
    this.tireState.rim = '';
    this.tireState.size = '';

    // Update UI
    this.updateUI();

    this.#sendEvent('tire_selector_select_type', {
      tire_type: this.#equipmentEventNames[value] || value,
    });
  }

  onMobileEquipmentChange(event: Event) {
    // Mobile equipment is a select and controls the actual equipment checkboxes
    const target = event.target as HTMLSelectElement;
    const value = target.value;

    this.tireState.equipment = value ? [value] : [];
    this.tireState.season = '';
    this.tireState.position = '';
    this.tireState.width = '';
    this.tireState.rim = '';
    this.tireState.size = '';

    // Update UI
    this.updateUI();

    this.#sendEvent('tire_selector_select_type', {
      tire_type: this.#equipmentEventNames[value] || value,
    });
  }

  onSeasonChange(event: Event) {
    const target = event.target as HTMLSelectElement;
    const value = target.value;

    // Update state
    this.tireState.season = value;

    // Update UI
    this.updateUI();

    this.#sendEvent('tire_selector_options', {
      tire_option: 'Tyre season',
      tire_option_value: value,
      tire_type: this.#getEventTireType(),
    });
  }

  onPositionChange(event: Event) {
    const target = event.target as HTMLSelectElement;
    const value = target.value;

    // Update state
    this.tireState.position = value;

    // Update UI
    this.updateUI();

    this.#sendEvent('tire_selector_options', {
      tire_option: 'Tyre position',
      tire_option_value: value,
      tire_type: this.#getEventTireType(),
    });
  }

  onWidthChange(event: Event) {
    const target = event.target as HTMLSelectElement;
    const value = target.value;

    // Update state
    this.tireState.width = value;

    // Update UI
    this.updateUI();

    this.#sendEvent('tire_selector_options', {
      tire_option: 'Tyre width',
      tire_option_value: value,
      tire_type: this.#getEventTireType(),
    });
  }

  onRimChange(event: Event) {
    const target = event.target as HTMLSelectElement;
    const value = target.value;

    // Update state
    this.tireState.rim = value;

    // Update UI
    this.updateUI();

    this.#sendEvent('tire_selector_options', {
      tire_option: 'Tyre diameter',
      tire_option_value: value,
      tire_type: this.#getEventTireType(),
    });
  }

  onSizeChange(event: Event) {
    const target = event.target as HTMLSelectElement;
    const value = target.value;

    // Update state
    this.tireState.size = value;

    // Update UI
    this.updateUI();

    this.#sendEvent('tire_selector_options', {
      tire_option: 'Tyre size',
      tire_option_value: value,
      tire_type: this.#getEventTireType(),
    });
  }

  onFormSubmit(event: Event): void {
    super.onFormSubmit(event);

    this.#sendEvent('tire_selector_show_results');
  }

  onFormReset(event: Event) {
    super.onFormReset(event);

    // Reset state and update ui
    setTimeout(() => {
      this.tireState = this.getTireStateFromDom();
      this.updateUI();
    }, 1);
  }

  updateUI() {
    // Update quipment checked state
    if(this.tireDom.equipment.length) {
      this.tireDom.equipment.forEach((input) => {
        input.checked = this.tireState.equipment.includes(input.value);
      });

      // Update mobile equipment select
      // I guess we should set first as selected, since this is not a multiselect?
      for (const opt of this.tireDom.mobileEquipment.options) {
        opt.selected = !opt.hidden && this.tireState.equipment?.[0] === opt.value;
      }
    }

    // Update season options
    if (this.tireDom.season) {
      const products = this.filterProducts({ ...this.tireState, season: '' });
      const productSeasons = products.map((product) => product.season);
      for (const opt of this.tireDom.season.options) {
        opt.hidden = !!opt.value && !productSeasons.includes(opt.value);
        opt.selected = !opt.hidden && this.tireState.season === opt.value;
      }
    }

    const updateSizeSelect = (el, key) => {
      const sizes = this.filterProducts({ ...this.tireState, [key]: '' })
        .flatMap((product) => {
          return product.sizes
            ?.filter(size => {
              if(key !== 'position' && this.tireState.position && size.position !== this.tireState.position) {
                return false;
              }

              if(key !== 'width' && this.tireState.width && String(size.width) !== this.tireState.width) {
                return false;
              }

              if(key !== 'rim' && this.tireState.rim && String(size.rim) !== this.tireState.rim) {
                return false;
              }

              if(key !== 'size' && this.tireState.size && String(size.size) !== this.tireState.size) {
                return false;
              }

              return true;
            })
            .map((size) => String(size[key])) || [];
        });

      const uniqueSizes = [...new Set(sizes)];

      for (const opt of el.options) {
        opt.hidden = opt.value && !uniqueSizes.includes(opt.value);
        opt.selected = !opt.hidden && this.tireState[key] === opt.value;
      }
    };

    // Update position options
    if (this.tireDom.position) {
      updateSizeSelect(this.tireDom.position, 'position');
    }

    // Update width options
    if (this.tireDom.width) {
      updateSizeSelect(this.tireDom.width, 'width');
    }

    // Update rim options
    if (this.tireDom.rim) {
      updateSizeSelect(this.tireDom.rim, 'rim');
    }

    // Update size options
    if (this.tireDom.size) {
      updateSizeSelect(this.tireDom.size, 'size');
    }
  }

  filterProducts(values: TireState) {
    const filteredData = this.data.filter((product) => {
      // No matching equipment
      if (
        Array.isArray(values.equipment) &&
        values.equipment.length > 0 &&
        !product.equipment.some((id) => values.equipment.includes(id))
      ) {
        return false;
      }

      // No season match
      if (values.season && values.season !== product.season) {
        return false;
      }

      // No tyre position match
      if (
        values.position &&
        !product.sizes?.find((size) => String(size.position) === values.position)
      ) {
        return false;
      }

      // No tyre width match
      if (
        values.width &&
        !product.sizes?.find((size) => String(size.width) === values.width)
      ) {
        return false;
      }

      // No tyre rim match
      if (
        values.rim &&
        !product.sizes?.find((size) => String(size.rim) === values.rim)
      ) {
        return false;
      }
      // No tyre size match
      if (
        values.size &&
        !product.sizes?.find((size) => String(size.size) === values.size)
      ) {
        return false;
      }

      return true;
    });

    return filteredData;
  }

  /**
   * Get event tire type for analytics.
   * Basically its equipment event names joined by comma.
   *
   * @returns {string} Tire type string.
   */
  #getEventTireType() {
    const tireType = this.tireState.equipment
      .map((id) => this.#equipmentEventNames[id] || id)
      .join(', ');

    return tireType;
  }

  /**
   * Send tyre selector analytics event.
   *
   * @param {string} name Event name (eg. tire_selector_select_type).
   * @param {object} [data] Optional event data.
   */
  #sendEvent(name, data = {}) {
    const isAdmin = document.body.classList.contains('view-admin-editor');

    const eventObj = {
      event: name,
      ...data,
    };

    if(!isAdmin) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(eventObj);
    } else {
      console.log(`tire-selector-analytics: ${JSON.stringify(eventObj, null, 2)}`);
    }
  }
}
